import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingContext, NavigateContext, M_Table, BufferService } from 'src/M_Components'

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

const getField = (row, ...keys) => {
    if (!row) return undefined
    for (const key of keys) {
        if (!Object.prototype.hasOwnProperty.call(row, key)) continue
        const value = row[key]
        if (value !== undefined && value !== null && value !== '') return value
    }
    return undefined
}

const parseTransportJson = (row) => {
    const raw = getField(row, 'TransportDocumentJson', 'transportDocumentJson')
    if (!raw) return null
    try {
        return JSON.parse(raw)
    } catch {
        return null
    }
}

const getBookingRef = (row) => {
    const ref = getField(row, 'TransportDocumentReference', 'transportDocumentReference')
    if (ref) return ref

    const payload = parseTransportJson(row)
    const transport = payload?.EmitEblRequest?.TransportDocument
    if (!transport) return null

    return transport.TransportDocumentReference
        || transport.ConsignmentItems?.[0]?.CarrierBookingReference
        || null
}

export const EblDocumentList = ({ roles, tablesOrder = [], onTablesOrderUpdate }) => {
    const { t } = useTranslation()
    const ctx = useContext(LoadingContext)
    const navCtx = useContext(NavigateContext)
    const [refresh, setRefresh] = useState(false)

    const eblService = new BufferService('ebls')

    const toastMessages = (type, alert, string) => ctx.toastMessages(type, alert, string)

    const canUseActions = () => {
        const nameForRole = 'eblDocuments'
        const tempPerm = []
        roles.forEach((role) => {
            if (role.match(nameForRole)) tempPerm.push(role.split(nameForRole)[1])
        })
        return tempPerm.includes('List') || tempPerm.includes('Edit')
    }

    const getPermissions = (type) => {
        if (!canUseActions()) {
            if (type === 'Context') return []
            if (type === 'Buttons') return []
        }

        const permLines = [
            {
                label: t('LabelViewBL'),
                icon: 'pi pi-search',
                command: () => viewBL(getRowFromContext()),
                needLine: true
            },
            {
                label: t('Check eBL Status') || 'Check eBL Status',
                icon: 'pi pi-refresh',
                command: () => syncStatus(getRowFromContext()),
                needLine: true
            },
            {
                label: t('Reprocess eBL') || 'Reprocess eBL',
                icon: 'pi pi-replay',
                command: () => reprocess(getRowFromContext()),
                needLine: true
            }
        ]

        const permButtons = [
            {
                label: t('LabelViewBL'),
                element: 'default',
                source: '/icons/svgsDaNet/search.svg',
                style: { filter: 'invert(60%) sepia(93%) saturate(1397%) hue-rotate(169deg) brightness(83%) contrast(86%)' },
                command: (data) => viewBL(data),
                needLine: true
            },
            {
                label: t('Check eBL Status') || 'Check eBL Status',
                element: 'default',
                icon: <i className='pi pi-refresh iconTable' />,
                command: (data) => syncStatus(data),
                needLine: true
            },
            {
                label: t('Reprocess eBL') || 'Reprocess eBL',
                element: 'default',
                icon: <i className='pi pi-replay iconTable' />,
                command: (data) => reprocess(data),
                needLine: true
            }
        ]

        if (type === 'Context') return permLines
        if (type === 'Buttons') return permButtons
    }

    const getRowFromContext = () => {
        const tempData = sessionStorage.getItem('context')
        if (!tempData || tempData === 'undefined') return null
        try {
            return JSON.parse(tempData)
        } catch {
            return null
        }
    }

    const getRowId = (row) => getField(row, 'Id', 'id')
    const getRowIdBL = (row) => getField(row, 'IdBL', 'idBL')

    const isEblIntegrated = (row) => {
        if (!row) return false
        if (Object.prototype.hasOwnProperty.call(row, 'IsEblIntegrated')) return row.IsEblIntegrated === true
        if (Object.prototype.hasOwnProperty.call(row, 'isEblIntegrated')) return row.isEblIntegrated === true
        return false
    }

    const getRowStatus = (row) => getField(row, 'Status', 'status')

    const canCheckStatus = (row) =>
        row && getField(row, 'NexusEblId', 'nexusEblId', 'DocumentId', 'documentId')

    const canReprocess = (row) =>
        row && getRowStatus(row) === 'Failed' && !isEblIntegrated(row)

    const handleApiError = (res) => {
        if (res?.enabled === false) {
            toastMessages('warning', t('eBL') || 'eBL', res.message)
            return true
        }
        if (res?.name === 'Axios Error' || res?.response) {
            const data = res.response?.data
            const message = typeof data === 'string'
                ? data
                : data?.Message || data?.message || res.message || t('Error')
            toastMessages('error', '', message)
            return true
        }
        return false
    }

    const openViewBL = (blId, bookingRef) => {
        if (!blId || blId === EMPTY_GUID) {
            toastMessages('error', '', t('LabelFirstCreateBL'))
            return
        }

        // viewBL.jsx expects blContext.Id (same pattern as blList / bookingList navigation)
        sessionStorage.setItem('blContext', JSON.stringify({
            Id: blId,
            NrBooking: bookingRef || ''
        }))
        sessionStorage.setItem('operation', 'View')
        const label = `${t('LabelViewBL')} ${bookingRef || ''}`.trim()
        navCtx.newTab('go/viewBL', label)
    }

    const viewBL = (row) => {
        if (!row) {
            toastMessages('error', '', t('SelectedLine'))
            return
        }

        openViewBL(getRowIdBL(row), getBookingRef(row))
    }

    const syncStatus = (row) => {
        if (!row) {
            toastMessages('error', '', t('SelectedLine'))
            return
        }
        if (!canCheckStatus(row)) {
            toastMessages('error', '', t('eBL status not available yet') || 'eBL status is not available until the document is emitted to NEXUS.')
            return
        }

        const docId = getRowId(row)
        if (!docId) {
            toastMessages('error', '', t('SelectedLine'))
            return
        }

        ctx.setIsLoad(true)
        eblService.postTo(`documents/${docId}/sync-status`, {}).then((res) => {
            ctx.setIsLoad(false)
            if (handleApiError(res)) return

            const status = getField(res, 'Status', 'status') || getRowStatus(row)
            toastMessages('success', '', `${t('eBL status updated') || 'eBL status updated'}: ${status}`)
            setRefresh(true)
        })
    }

    const reprocess = (row) => {
        if (!row) {
            toastMessages('error', '', t('SelectedLine'))
            return
        }
        if (!canReprocess(row)) {
            toastMessages('error', '', t('Only failed eBL documents can be reprocessed') || 'Only failed eBL documents can be reprocessed.')
            return
        }

        const docId = getRowId(row)
        if (!docId) {
            toastMessages('error', '', t('SelectedLine'))
            return
        }

        ctx.setIsLoad(true)
        eblService.postTo(`documents/${docId}/reprocess`, {}).then((res) => {
            ctx.setIsLoad(false)
            if (handleApiError(res)) return

            toastMessages('success', '', t('eBL reprocessed successfully') || 'eBL reprocessed successfully.')
            setRefresh(true)
        })
    }

    const blLink = (blId, label) => {
        if (!blId || blId === EMPTY_GUID) return label || ''

        return (
            <span
                role="link"
                tabIndex={0}
                style={{ color: 'var(--primary-color, #1976d2)', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={(e) => {
                    e.stopPropagation()
                    openViewBL(blId, label)
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        openViewBL(blId, label)
                    }
                }}
            >
                {label || blId}
            </span>
        )
    }

    const blReferenceColumn = (rowData) => {
        const blId = getRowIdBL(rowData)
        const label = getBookingRef(rowData) || (blId ? t('LabelViewBL') : '')
        if (!label && !blId) return ''
        return blLink(blId, label)
    }

    const getContextLine = (e) => {
        if (e) {
            sessionStorage.setItem('context', JSON.stringify(e))
        } else {
            sessionStorage.removeItem('context')
        }
    }

    const getSelectedLine = (e) => {
        if (e?.length) {
            sessionStorage.setItem('lines', JSON.stringify(e))
        } else {
            sessionStorage.removeItem('lines')
        }
    }

    return (
        <M_Table
            entityName={'eblDocument'}
            rolePermissions={roles}
            tablesOrder={tablesOrder}
            onTablesOrderUpdate={onTablesOrderUpdate}
            pageUrl={'eblDocuments'}
            removeContextItems={['Create', 'Edit', 'Delete', 'Copy']}
            lineContext={getPermissions('Context')}
            extraButtons={getPermissions('Buttons')}
            onPassContext={getContextLine}
            onPassLine={getSelectedLine}
            columnTemplates={[
                { field: 'TransportDocumentReference', body: blReferenceColumn }
            ]}
            refresh={refresh}
            setRefresh={setRefresh}
        />
    )
}
